import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';
import { visibleForSession } from './sessionScope';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PLATFORMS = ['META', 'TIKTOK'];
export const SCRIPT_LENGTHS = ['15-30', '30-45', '45-60'];

/**
 * The Script Generation step's behaviour — ONE implementation, two screens.
 *
 * WAVE 3, SCREEN 4. What the sweep found, and four of the five are the same
 * family screens 1-3 carried, because these steps were built as copies:
 *
 *   1. `fetchScripts` swallowed every error into `console.error`, so a failed
 *      read and an offer with no scripts were one blank list. Rule 10.
 *   2. A NEW session never fetched at all and nothing said so.
 *   3. Nothing was scoped to a session — live, **3 of 288 scripts carry one**,
 *      and one offer holds 45.
 *   4. Generation appended with no notice of repeats.
 *   5. Two failure paths used `alert()`.
 *
 * AND ONE THIS SCREEN HAD ON ITS OWN: `fetchScripts` was
 * `useCallback(..., [offer.offer_id])` while reading `angleId` from the closure.
 * MA-3 gave each track its own angle, so switching tracks left the callback
 * pinned to the angle it was created with — **track 2 would have re-read track
 * 1's scripts**, silently, and only on a re-fetch. A missing dependency is not
 * a style problem; it is a stale answer with no error.
 */
export function useScripts({ offer, sessionId, isNewSession, sessionStartTime,
                             angleId, onComplete, refreshKey = 0 }) {
  const [allScripts, setAllScripts] = useState([]);
  const [platform, setPlatform] = useState('META');
  const [scriptLength, setScriptLength] = useState('30-45');
  const [feedback, setFeedback] = useState('');
  const [generating, setGenerating] = useState(false);
  const [busyScript, setBusyScript] = useState(null);

  const [loadState, setLoadState] = useState('idle');
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [note, setNoteState] = useState(null);
  const setNote = (text) => {
    setNoteState((prev) =>
      !text ? null
      : prev && prev.text === text ? { text, count: prev.count + 1 }
      : { text, count: 1 });
  };

  // Assigned before the handlers that close over it, because `confirmAll` reads
  // it when pressed rather than when defined.
  const view = visibleForSession(allScripts, sessionId, { isNewSession, sessionStartTime });

  const fetchScripts = useCallback(async () => {
    setLoadState('loading');
    try {
      const params = {};
      if (angleId) params.angle_id = angleId;
      const res = await axios.get(`${API}/offers/${offer.offer_id}/scripts`,
                                  Object.keys(params).length ? { params } : undefined);
      setAllScripts(res.data.scripts || []);
      setLoadState('ok');
      setLoadError(null);
    } catch (error) {
      setLoadState('failed');
      setLoadError(errorText(error));
    }
    // `angleId` IS a dependency — see the module docstring. Leaving it out is
    // what pinned this callback to one track's angle.
  }, [offer.offer_id, angleId]);

  useEffect(() => { fetchScripts(); }, [fetchScripts, refreshKey]);

  const generateScripts = async () => {
    setGenerating(true);
    setActionError(null);
    try {
      const res = await axios.post(`${API}/offers/${offer.offer_id}/scripts/generate`, {
        platform,
        script_length: scriptLength,
        feedback: feedback || undefined,
        session_id: sessionId || undefined,
        angle_id: angleId || undefined,
      });
      const returned = res.data.scripts || [];
      setFeedback('');

      // Counted from the list the SCREEN draws, not from the response — the
      // Hooks step shipped saying "Added 5" over an empty grid because those
      // two had diverged.
      const opts = { isNewSession, sessionStartTime };
      const before = visibleForSession(allScripts, sessionId, opts).rows.length;
      const after = visibleForSession([...allScripts, ...returned], sessionId, opts).rows.length;
      const appeared = after - before;

      if (returned.length) setAllScripts((prev) => [...prev, ...returned]);

      // THE SERVER'S OWN ACCOUNT FIRST, when it has one. It knows things this
      // screen cannot: that the answer took two attempts, that a script in the
      // batch was malformed, that a selected hook came back without a script.
      // Dropping it would turn a recovered or partial run into a clean-looking
      // one (Founder's two failures, 2026-09-10).
      const serverNote = res.data?.partial_note;
      setNote(
        serverNote
          ? `${serverNote} ${returned.length} script${returned.length === 1 ? '' : 's'} saved.`
          : returned.length === 0
          ? 'The model returned no scripts. Nothing was added.'
          : appeared === 0
            ? `Saved ${returned.length} script${returned.length > 1 ? 's' : ''}, but `
              + `none are shown here — this view is scoped to the current session. `
              + `They are on the offer.`
            : appeared < returned.length
              ? `Saved ${returned.length}; ${appeared} shown here.`
              : `Added ${returned.length} script${returned.length > 1 ? 's' : ''}.`);
    } catch (error) {
      setActionError(errorText(error));
    } finally {
      setGenerating(false);
    }
  };

  /** Replace one script in place, whatever changed it. */
  const merge = (updated) => {
    if (!updated?.script_id) return;
    setAllScripts((prev) => prev.map(
      (s) => (s.script_id === updated.script_id ? { ...s, ...updated } : s)));
  };

  const rewriteSection = async (scriptId, section, text) => {
    const feedbackText = (text || '').trim();
    if (feedbackText.length < 3) return false;
    setBusyScript(`${scriptId}::${section}`);
    setActionError(null);
    try {
      const res = await axios.post(
        `${API}/scripts/${scriptId}/sections/${section}/rewrite`,
        { feedback: feedbackText });
      merge(res.data.script);
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusyScript(null);
    }
  };

  /**
   * Why a section says what it says.
   *
   * Returns `{ ok, text }` rather than writing a sentence into the same place
   * the real explanation goes. The old screen stored "Could not load reasoning
   * right now." INTO the explanations map, so a failed read rendered in the
   * same slot, in the same style, as a real explanation — a failure wearing the
   * answer's clothes.
   */
  const explainSection = async (scriptId, section) => {
    try {
      const res = await axios.get(
        `${API}/scripts/${scriptId}/sections/${section}/explain`);
      return { ok: true, text: res.data.explanation };
    } catch (error) {
      return { ok: false, text: errorText(error) };
    }
  };

  /**
   * Edit one section in the operator's own words — no model in the loop.
   *
   * The Founder, 2026-09-09: there was "nowhere for me to edit the visual
   * direction". Rewrite asks a model to redo the section from a note and stays
   * the encouraged path; this is for when the operator already knows the shot.
   */
  const editSection = async (scriptId, section, changes) => {
    setActionError(null);
    try {
      const res = await axios.patch(
        `${API}/scripts/${scriptId}/sections/${section}`, changes);
      merge(res.data.script);
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    }
  };

  /** The permanent name, and the fact that it is fixed. Read-only. */
  const loadName = async (scriptId) => {
    try {
      const res = await axios.get(`${API}/scripts/${scriptId}/name`);
      return { ok: true, ...res.data };
    } catch (error) {
      return { ok: false, error: errorText(error) };
    }
  };

  /**
   * Confirm every script in view that has not been confirmed, then report.
   *
   * FOUNDER RULING 2026-09-09 (CREATIVE-NO-FOUNDER-GATE): "Remove the 'Approve
   * for production' button entirely — it isn't actually gating anything today
   * anyway... Fold its two legitimate side effects (minting the permanent
   * body/CTA numbers, saving the script to Drive) into a plain 'Confirm &
   * continue' button on that step, matching every other step in the wizard."
   *
   * So this is ONE button at the foot of the step, not a per-script gesture:
   * the same shape as step 1's "Confirm & continue", step 2's "Continue with N
   * angles" and step 3's "Continue".
   *
   * A PARTIAL RESULT DOES NOT ADVANCE. Two confirmed and one refused is neither
   * done nor failed, and a script that did not get its permanent number would
   * reach production naming as a placeholder `B1` — silently. The caller
   * advances only on a clean pass, and the failures are named.
   */
  const confirmAll = async () => {
    const pending = view.rows.filter((s) => !s.approved_for_production);
    if (pending.length === 0) return { confirmed: 0, failures: [], clean: true };

    setBusyScript('__all__');
    setActionError(null);
    let confirmed = 0;
    const failures = [];
    for (const script of pending) {
      const label = script.body_element_number
                 || String(script.script_id).slice(0, 10);
      try {
        const res = await axios.post(`${API}/scripts/${script.script_id}/approve`);
        if (res.data?.status === 'approved') {
          merge({ script_id: script.script_id, approved_for_production: true,
                  status: 'APPROVED_FOR_PRODUCTION',
                  body_element_number: res.data?.body_element_number
                                    || script.body_element_number });
          confirmed += 1;
        } else {
          // A 200 that is not a confirmation is not a confirmation.
          failures.push({ label,
                          detail: `the server answered "${res.data?.status}"` });
        }
      } catch (error) {
        failures.push({ label, detail: errorText(error) });
      }
    }
    setBusyScript(null);
    return { confirmed, failures, clean: failures.length === 0 };
  };

  const approveScript = async (scriptId) => {
    setBusyScript(scriptId);
    setActionError(null);
    try {
      const res = await axios.post(`${API}/scripts/${scriptId}/approve`);
      if (res.data.status === 'approved') {
        merge({ script_id: scriptId, approved_for_production: true,
                status: 'APPROVED_FOR_PRODUCTION' });
        return true;
      }
      // A 200 that is not an approval is not an approval. Before this the
      // screen simply did nothing and the operator re-clicked.
      setActionError(
        `The script was not approved: the server answered "${res.data.status}".`);
      return false;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusyScript(null);
    }
  };



  return {
    scripts: view.rows,
    hiddenOtherSession: view.hiddenOtherSession,
    hiddenUnstamped: view.hiddenUnstamped,
    predateNote: view.predateNote,
    allCount: allScripts.length,
    platform, setPlatform, scriptLength, setScriptLength,
    feedback, setFeedback,
    loadState, loadError, retryLoad: fetchScripts,
    generating, generateScripts,
    busyScript, rewriteSection, explainSection, approveScript, confirmAll,
    editSection, loadName,
    actionError, setActionError, note,
    proceed: onComplete,
  };
}
